import { Loader2 } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/common/ui/alert-dialog";
import { Input } from "@/common/ui/input";

import type { CreateFlagInput } from "./feature-flags-types";

interface CreateFlagDialogProps {
  open: boolean;
  /**
   * Must return a Promise so the dialog can show a loading state while
   * the create request is in flight. Resolve to close the dialog; reject
   * to keep the dialog open with the user's input intact (handled by the
   * dialog's catch block — the caller's error UI is still free to show).
   */
  onConfirm: (input: CreateFlagInput) => Promise<void>;
  onCancel: () => void;
}

export function CreateFlagDialog({
  open,
  onConfirm,
  onCancel,
}: CreateFlagDialogProps): React.ReactElement {
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [keyError, setKeyError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const keyPattern = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

  async function handleConfirm(): Promise<void> {
    if (pending) return;
    const trimmedKey = key.trim();
    if (trimmedKey.length < 2) {
      setKeyError("Key must be at least 2 characters");
      return;
    }
    if (!keyPattern.test(trimmedKey)) {
      setKeyError("Key must be lowercase alphanumeric with hyphens (e.g. my-flag)");
      return;
    }
    setSubmitError(null);
    setPending(true);
    try {
      await onConfirm({
        key: trimmedKey,
        description: description.trim() || undefined,
      });
      // Only reset on success — on failure we keep the form so the user
      // can adjust + retry without retyping.
      setKey("");
      setDescription("");
      setKeyError(null);
    } catch (err) {
      // Surface the error inside the dialog. The page-level error banner
      // sits behind the AlertDialog overlay's focus trap, so without this
      // the user sees the Create button re-enable with no explanation.
      setSubmitError(err instanceof Error ? err.message : "Failed to create flag");
    } finally {
      setPending(false);
    }
  }

  function handleCancel(): void {
    if (pending) return;
    setKey("");
    setDescription("");
    setKeyError(null);
    setSubmitError(null);
    onCancel();
  }

  return (
    <AlertDialog open={open}>
      <AlertDialogContent data-testid="create-flag-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Create Feature Flag</AlertDialogTitle>
          <AlertDialogDescription>
            Create a new feature flag. The key cannot be changed after creation.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="flag-key" className="text-sm font-medium">
              Flag Key
            </label>
            <Input
              id="flag-key"
              data-testid="flag-key-input"
              placeholder="my-new-feature"
              value={key}
              disabled={pending}
              onChange={(e) => {
                setKey(e.target.value);
                setKeyError(null);
              }}
            />
            {keyError && <p className="text-xs text-destructive">{keyError}</p>}
          </div>
          <div className="space-y-2">
            <label htmlFor="flag-description" className="text-sm font-medium">
              Description
            </label>
            <Input
              id="flag-description"
              data-testid="flag-description-input"
              placeholder="What does this flag control?"
              value={description}
              disabled={pending}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        {submitError && (
          <div
            role="alert"
            data-testid="create-flag-error"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {submitError}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="create-flag-cancel"
            onClick={handleCancel}
            disabled={pending}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="create-flag-confirm"
            onClick={() => {
              void handleConfirm();
            }}
            disabled={pending}
          >
            {pending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating…
              </>
            ) : (
              "Create"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
